// The three screens that open over the shell — „Twoje linie", „Warstwy" and
// „Jak się poruszać" — plus the intro, the footer and what this browser
// remembers (SPEC-redesign §5.7, §5.9, §4.6).
//
// `viewState.screen` is what opens them and `viewState.closeScreen()` is what
// the `‹` calls. Opening is one history entry; **every change made inside a
// screen is a `'replace'` write and therefore survives `‹`, Esc and Back**
// (D-b). The prototype pushes on open and replaces inside, then closes with a
// bare `history.back()`, which is why a resident who adds a line or curates
// their sources loses the lot on the way out — reproduced by hand twice
// (`prototype-dossier.md` §9.8, §9.9).
import './screens.css';
import { onSnapshot, type Snapshot } from '../data/data.ts';
import { derived } from '../derived.ts';
import { mount } from '../shell.ts';
import { viewState, type Screen, type ViewState } from '../state/viewstate.ts';
import { paintFooter } from './footer.ts';
import { helpScreen } from './help.ts';
import { startIntro } from './intro.ts';
import { countOn, layersScreen } from './layers.ts';
import { freshnessLabel, pickerScreen, type PickerState } from './picker.ts';

/** The title each screen wears in its header, and therefore its accessible name. */
const TITLES: Record<Screen, string> = {
  linie: 'Twoje linie',
  warstwy: 'Warstwy',
  pomoc: 'Jak się poruszać',
};

/**
 * A snapshot before any has arrived. A screen is reachable from a pasted link on
 * the first frame, so „Twoje linie" has to render against something — and a
 * source we have heard nothing from is starting, never down (§7.2, D-2).
 */
const NOTHING_YET: Snapshot = {
  items: [],
  health: {
    ztm: { state: 'starting', error: null, updatedAt: 0 },
    ztmk: { state: 'starting', error: null, updatedAt: 0 },
    pl: { state: 'starting', error: null, updatedAt: 0 },
    aq: { state: 'starting', error: null, updatedAt: 0 },
  },
  ztmUpdatedAt: 0,
  // ZTM's own clock, which we do not have either: nothing has loaded, so there
  // is no feed stamp to report and `null` is the honest answer rather than 0.
  ztmFeedTs: null,
  serverReachable: true,
};

let snapshot: Snapshot = NOTHING_YET;

/** „Twoje linie" keeps its filter and its query to itself: neither is in the address. */
const picker: PickerState = {
  filter: 'all',
  query: '',
  onChange: () => {
    paint();
  },
};

/**
 * Which control opened the screen, so focus goes back to it when the screen
 * closes (§8). It is remembered as a **selector**, not as an element: the footer
 * is rebuilt on every change, so the button that was clicked is usually gone by
 * the time it would be focused again, and an element reference would silently
 * drop the resident at the top of the document.
 */
let opener: string | null = null;

function openerKey(el: Element | null): string | null {
  if (!(el instanceof HTMLElement)) return null;
  if (el.id) return `#${CSS.escape(el.id)}`;
  const label = el.getAttribute('aria-label');
  return label ? `[aria-label="${CSS.escape(label)}"]` : null;
}

const bodyOf = (screen: Screen, view: ViewState): HTMLElement => {
  if (screen === 'linie') return pickerScreen(snapshot, view, picker);
  if (screen === 'warstwy') {
    // What the wheel would draw with the silences lifted, so „Warstwy" can
    // never claim more than the disc shows (D-4) and never collapses to zero
    // the moment a voice is switched off (#163). Both readings come off the ONE
    // derivation of this change (`src/derived.ts`, ADR-0016) — a derivation of
    // this screen's own, from a centre and a clock of its own, is exactly the
    // drift `src/state/centre.ts` and one clock exist to prevent.
    // Before the first snapshot there is nothing derived and nothing to count.
    const state = derived.get();
    return layersScreen(snapshot, view, state.derived?.markedUnsilenced ?? [], state.now);
  }
  return helpScreen();
};

/** The breakpoint §5.10 sets; a CSS media query, never a device test (ruling 12). */
const desktop = () => matchMedia('(min-width: 900px)');

/**
 * **What a screen covers is what it may deaden.**
 *
 * On the phone it covers the whole frame, top bar included, so it is a modal
 * dialog: the shell behind it and the skip link in front of it both go inert.
 *
 * At the desktop breakpoint it covers the 480 px axis column alone. The top bar
 * and the circle stay visible beside it, so they stay usable: only `.column` is
 * inert, and the screen is a panel rather than a modal. A control that looks
 * live and cannot be reached is worse than one that is plainly gone.
 */
function setInert(on: boolean) {
  const wide = desktop().matches;
  const covered = on ? (wide ? ['.column'] : ['.shell', '.skip']) : [];
  for (const selector of ['.shell', '.skip', '.column']) {
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) el.inert = covered.includes(selector);
  }
}

function wrapBody(inner: HTMLElement): HTMLElement {
  const body = document.createElement('div');
  body.className = 'screen__body';
  body.append(inner);
  return body;
}

/** The right-hand side of a header: the freshness reading, or the layer counter. */
function aside(screen: Screen, view: ViewState): HTMLElement | null {
  if (screen === 'linie') {
    const el = document.createElement('span');
    el.className = 'screen__aside';
    el.textContent = freshnessLabel(snapshot, Date.now());
    return el;
  }
  if (screen === 'warstwy') {
    const el = document.createElement('span');
    el.className = 'screen__aside';
    el.textContent = `${countOn(view)} z 4 włączonych`;
    return el;
  }
  return null;
}

function paint() {
  const view = viewState.get();
  const host = mount('overlay');

  paintFooter(mount('footer'), view);

  const screen = view.screen;
  if (!screen) {
    // Whatever the intro put here — the reduced-motion hint strip — is its own
    // to remove; only the dialog belongs to this function.
    host.querySelector('.screen')?.remove();
    setInert(false);
    return;
  }

  const title = TITLES[screen];
  const open = host.querySelector('.screen');
  // A re-render must not take focus off a control the resident is using: the
  // number field loses its caret and a filter button loses its ring. An already
  // open screen therefore keeps its shell and swaps only its body.
  if (open instanceof HTMLElement && open.dataset.screen === screen) {
    open.querySelector('.screen__body')?.replaceWith(wrapBody(bodyOf(screen, view)));
    const side = aside(screen, view);
    if (side) open.querySelector('.screen__aside')?.replaceWith(side);
    return;
  }

  const dialog = document.createElement('div');
  dialog.className = 'screen';
  dialog.dataset.screen = screen;
  dialog.setAttribute('role', 'dialog');
  // Named by a real heading rather than by a string of its own: the screen is
  // then reachable by heading as well as by role, and the name cannot drift
  // from what the `‹` button says because both come from `TITLES`.
  const headingId = `screen-title-${screen}`;
  dialog.setAttribute('aria-labelledby', headingId);

  const heading = document.createElement('h2');
  heading.className = 'visually-hidden';
  heading.id = headingId;
  heading.textContent = title;

  const header = document.createElement('div');
  header.className = 'screen__header';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'screen__back';
  // `‹ <Title>` is `closeScreen()`, never a bare `history.back()`: closing must
  // not undo a step taken on top of the screen, and must leave no entry Back
  // could re-open it from (§4.2, D-1).
  back.textContent = `‹ ${title}`;
  back.addEventListener('click', () => {
    viewState.closeScreen();
  });
  header.append(back);
  const side = aside(screen, view);
  if (side) header.append(side);

  dialog.append(heading, header, wrapBody(bodyOf(screen, view)));
  host.replaceChildren(dialog);
  // Order matters: `inert` blurs whatever it covers, so the opener has to have
  // been remembered before this line, not after it.
  setModality();
  back.focus();
}

/**
 * Modal on the phone, a panel on the desktop — and it has to follow a window
 * being resized across the breakpoint while a screen is open, or the page is
 * left either half-deadened or claiming a modality it no longer has.
 */
function setModality() {
  const dialog = mount('overlay').querySelector('.screen');
  if (!(dialog instanceof HTMLElement)) {
    setInert(false);
    return;
  }
  setInert(true);
  if (desktop().matches) dialog.removeAttribute('aria-modal');
  else dialog.setAttribute('aria-modal', 'true');
}

// Persisting `lines`, `muted` and `radius` is deliberately **not** here. It has
// one owner in the view-state layer, where a user-originated `viewState.set()`
// can be told apart from state that arrived in the address — a mirror on every
// change would let a shared link overwrite the resident's own lines, which is
// the behaviour the old `lines.spec.ts` guarded („a hash opens the map already
// filtered, whatever this browser remembers"). This file's only memory keys are
// the intro's `runs`, in `src/screens/intro.ts`.

export function mountScreens(): void {
  const host = mount('overlay');
  mount('footer');

  // The seam publishes to every module that draws, and calls a late subscriber
  // at once with the last snapshot (§4.3) — so there is nothing to thread
  // through the shell and no second poll.
  onSnapshot((next) => {
    snapshot = next;
    paint();
  });

  viewState.subscribe((now, before) => {
    const closing = before.screen != null && now.screen == null;
    // Remembered before `paint()`, because the `inert` it sets blurs the button
    // that was just pressed and `document.activeElement` would then be the body.
    if (before.screen == null && now.screen != null) opener = openerKey(document.activeElement);
    paint();
    // The screen that just closed hands focus back to the control that opened
    // it: a resident who tabbed to the footer's „Warstwy" button lands on it
    // again rather than at the top of the document (§8).
    if (closing && opener) {
      const back = document.querySelector(opener);
      if (back instanceof HTMLElement) back.focus();
      opener = null;
    }
  });

  // A resize across 900 px changes what the screen covers, and therefore what it
  // may deaden and whether it is modal at all (§5.10, ruling 12).
  desktop().addEventListener('change', setModality);

  paint();
  startIntro(host);
}
